import { Loader2, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/shared/ui/button/Button";
import { Dialog, DialogContent, DialogTitle } from "@/shared/ui/dialog/Dialog";
import { Input } from "@/shared/ui/input/Input";
import { Skeleton } from "@/shared/ui/skeleton/Skeleton";

import {
  useGetActionCatalogQuery,
  useGetGlobalSearchQuery,
  type ActionDescriptor,
  type SearchHit,
} from "../api/searchApi";
import { buildAction, openTarget, TYPE_LABEL, TYPE_ORDER } from "../lib/actions";
import {
  activeFacetChips,
  activeFacetCount,
  applyFacets,
  EMPTY_FACETS,
  facetGroups,
  toggleFacet,
  type FacetKey,
  type FacetState,
} from "../lib/facets";
import { ActionPanel } from "./ActionPanel";
import { FacetRail } from "./FacetRail";
import { Chip, ResultRow } from "./ResultRow";

const NO_HITS: SearchHit[] = [];

/**
 * Global command-palette search (dev `components/common/GlobalSearch`). Mount
 * once in the app shell (Topbar). A header field is always visible; ⌘K / Ctrl-K
 * focuses it, and Enter opens the results dialog. Results + per-hit actions are
 * role-scoped server-side; the facet rail narrows the loaded result set
 * client-side (see `lib/facets`).
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [facets, setFacets] = useState<FacetState>(EMPTY_FACETS);
  const [panel, setPanel] = useState<{ hit: SearchHit; d: ActionDescriptor } | null>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const dialogInputRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl-K focuses the header search field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        headerInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Open the results dialog once a term has been entered (Enter / Search click).
  const submitSearch = useCallback(() => {
    const q = term.trim();
    if (q.length < 2) return;
    setDebounced(q);
    setOpen(true);
  }, [term]);

  // Debounce the query (~300ms).
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  // A new term is a new result set with its own facet values → start unfiltered.
  useEffect(() => {
    setFacets(EMPTY_FACETS);
  }, [debounced]);

  // Reset on close.
  useEffect(() => {
    if (!open) {
      setPanel(null);
      setFacets(EMPTY_FACETS);
      return;
    }
    // Focus the dialog's search field on open (rule-compliant replacement for autoFocus).
    const id = window.setTimeout(() => dialogInputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const canSearch = open && debounced.length >= 2;
  const { data, isFetching, isError } = useGetGlobalSearchQuery(
    { term: debounced, limit: 20 },
    { skip: !canSearch }
  );
  const { data: catalog } = useGetActionCatalogQuery(undefined, { skip: !open });

  const allHits = data?.results ?? NO_HITS;
  const filtered = useMemo(() => applyFacets(allHits, facets), [allHits, facets]);
  const rail = useMemo(() => facetGroups(allHits, facets), [allHits, facets]);
  const chips = useMemo(() => activeFacetChips(facets), [facets]);
  const grouped = useMemo(
    () =>
      TYPE_ORDER.map((t) => ({ type: t, hits: filtered.filter((h) => h.type === t) })).filter(
        (g) => g.hits.length
      ),
    [filtered]
  );

  const toggle = useCallback(
    (key: FacetKey, value: string) => setFacets((f) => toggleFacet(f, key, value)),
    []
  );
  const clearAll = useCallback(() => setFacets(EMPTY_FACETS), []);

  const runAction = useCallback(
    (hit: SearchHit, d: ActionDescriptor) => {
      const built = buildAction(hit, d);
      if ("navigateTo" in built) {
        if (built.navigateTo) {
          setOpen(false);
          navigate(built.navigateTo);
        }
        return;
      }
      setPanel({ hit, d }); // API action → open the result panel
    },
    [navigate]
  );

  // Row click / Enter — navigate to the hit itself. Works even when the
  // action catalog is empty or unavailable (rows must always open).
  const openHit = useCallback(
    (hit: SearchHit) => {
      const to = openTarget(hit);
      if (!to) return;
      setOpen(false);
      navigate(to);
    },
    [navigate]
  );

  const resultList = grouped.map((g) => (
    <div key={g.type} className="mb-2">
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-1.5">
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          {TYPE_LABEL[g.type]}
        </span>
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {g.hits.length}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
      {g.hits.map((hit) => (
        <ResultRow
          key={`${hit.type}-${hit.id}-${hit.hierarchyPath}`}
          hit={hit}
          actions={(catalog?.actions?.[hit.type] ?? []).filter((d) =>
            hit.availableActions.includes(d.key)
          )}
          onAction={runAction}
          onOpen={openHit}
        />
      ))}
    </div>
  ));

  let body: React.ReactNode;
  if (isError) {
    body = (
      <div className="px-3 py-8 text-center text-sm text-destructive">
        Search is temporarily unavailable.
      </div>
    );
  } else if (debounced.length < 2) {
    body = (
      <div className="px-3 py-10 text-center text-sm text-muted-foreground">
        Type a name to search across clients, employers, groups, billing accounts, agencies &
        agents.
      </div>
    );
  } else if (isFetching) {
    body = (
      <div className="space-y-2 px-2 py-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-3">
            <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-2.5 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (!data || data.totalCount === 0) {
    body = (
      <div className="flex flex-col items-center gap-2 px-3 py-12 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Search className="h-5 w-5" />
        </span>
        <p className="text-sm font-medium text-foreground">No matches for “{debounced}”</p>
        <p className="text-xs text-muted-foreground">Try a different name or ID.</p>
      </div>
    );
  } else {
    body = (
      <div className="grid grid-cols-1 md:grid-cols-[224px_minmax(0,1fr)]">
        <div className="max-h-[24vh] overflow-y-auto md:max-h-[58vh]">
          <FacetRail
            groups={rail}
            activeCount={activeFacetCount(facets)}
            onToggle={toggle}
            onClearAll={clearAll}
          />
        </div>
        <div className="flex max-h-[58vh] min-h-0 flex-col">
          {chips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-muted/40 px-4 py-2">
              {chips.map((c) => (
                <Chip key={`${c.key}:${c.value}`} active onClick={() => toggle(c.key, c.value)}>
                  {c.label}
                  <X className="ml-1 h-3 w-3" aria-hidden="true" />
                </Chip>
              ))}
              <Button
                type="button"
                variant="ghost"
                onClick={clearAll}
                className="h-auto px-2 py-1 text-[11px] text-muted-foreground hover:bg-transparent hover:text-foreground hover:underline"
              >
                Clear all
              </Button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-2.5 py-2.5">
            {grouped.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-3 py-12 text-center">
                <p className="text-sm font-medium text-foreground">Nothing matches these filters</p>
                <Button type="button" variant="outline" size="sm" onClick={clearAll}>
                  Clear filters
                </Button>
              </div>
            ) : (
              resultList
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header search field — dialog opens only after a term is entered */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
        className="group flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5 text-sm text-muted-foreground focus-within:border-input focus-within:bg-background hover:border-input hover:bg-background transition w-full max-w-md"
      >
        <Button
          type="submit"
          variant="ghost"
          size="icon"
          aria-label="Search"
          className="h-5 w-5 shrink-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
        >
          <Search className="h-4 w-4" />
        </Button>
        <Input
          ref={headerInputRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search clients, billing accounts, agents…"
          aria-label="Search clients, billing accounts, agents"
          className="min-w-0 flex-1 h-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <span className="ml-auto hidden md:inline rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          ⌘K
        </span>
      </form>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showClose={false}
          size="full"
          className="w-[95vw] max-w-5xl p-0 gap-0 overflow-hidden rounded-2xl top-[10%] translate-y-0"
        >
          <DialogTitle className="sr-only">Global search</DialogTitle>

          {panel ? (
            <ActionPanel
              hit={panel.hit}
              d={panel.d}
              onBack={() => setPanel(null)}
              onNavigate={(to) => {
                setOpen(false);
                navigate(to);
              }}
            />
          ) : (
            <>
              {/* Search input */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
                <Search className="h-[18px] w-[18px] text-primary shrink-0" />
                <Input
                  ref={dialogInputRef}
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  onKeyDown={(e) => {
                    // Footer promises "↵ open" — Enter opens the top (filtered) result.
                    if (e.key === "Enter") {
                      const first = grouped[0]?.hits[0];
                      if (first) openHit(first);
                    }
                  }}
                  placeholder="Search clients, billing accounts, agents…"
                  aria-label="Search clients, billing accounts, agents"
                  className="flex-1 h-auto border-0 bg-transparent p-0 text-base shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                {isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setOpen(false)}
                  className="h-7 w-7"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {body}

              {/* Footer */}
              <div className="flex items-center justify-between px-5 py-2.5 border-t border-border bg-muted/60 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <kbd className="rounded border border-border bg-background px-1.5 py-0.5 font-sans text-[10px] text-muted-foreground">
                    ↵
                  </kbd>
                  open
                  <span className="mx-1 text-border">·</span>
                  <kbd className="rounded border border-border bg-background px-1.5 py-0.5 font-sans text-[10px] text-muted-foreground">
                    esc
                  </kbd>
                  close
                </span>
                {data && data.totalCount > 0 && (
                  <span className="font-medium text-foreground">
                    {filtered.length === data.totalCount
                      ? `${data.totalCount} results`
                      : `${filtered.length} of ${data.totalCount} results`}
                  </span>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default GlobalSearch;
